import { router } from "@inertiajs/react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ResponsiveDrawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, } from "@/components/ui/drawer"
import type { MenuGridItem } from "@/components/features/menu/menu-grid"
import { t } from "@/lib/i18n"
// ─── Helpers ───────────────────────────────────────────────────────────────────
function formatPrice(price: string, currency: string) {
    const amount = Number(price)
    if (Number.isNaN(amount))
        return `${price} ${currency.toUpperCase()}`
    return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: currency.toUpperCase(),
    }).format(amount)
}
// ─── Component ─────────────────────────────────────────────────────────────────
export function MenuItemDetailDrawer({ item, open, onOpenChange, }: {
    item: MenuGridItem | null
    open: boolean
    onOpenChange: (open: boolean) => void
}) {
    if (!item)
        return null
    const isSoldOut = item.status === "sold_out"
    const extraGroups = item.extraGroups ?? []
    return (<ResponsiveDrawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader className="pb-2">
          <DrawerTitle>{item.name}</DrawerTitle>
          <DrawerDescription asChild>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="text-sm font-semibold text-foreground">
                {formatPrice(item.price, item.currency)}
              </span>
              {item.station && (<Badge variant="outline" className="capitalize">
                  {item.station.replace(/_/g, " ")}
                </Badge>)}
              {isSoldOut && (<Badge variant="destructive">{t("ui.react.features.staff.menu_item_detail_drawer.soldout")}</Badge>)}
            </div>
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4">

          {/* Image */}
          {item.imageUrl && (<img src={item.imageUrl} alt={item.name} className="w-full rounded-lg object-cover max-h-48"/>)}

          {/* Description */}
          {item.description && (<p className="text-sm text-muted-foreground leading-relaxed">
              {item.description}
            </p>)}

          {/* Extras */}
          {extraGroups.length > 0 && (<div className="space-y-3">
              <p className="text-sm font-medium">{t("ui.react.features.staff.menu_item_detail_drawer.modifiers_available")}</p>
              {extraGroups.map((group) => (<div key={group.id} className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">{group.name}</p>
                  <ul className="divide-y rounded-md border">
                    {group.extras.map((extra) => (<li key={extra.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-muted-foreground">{extra.name}</span>
                        <span className={cn("font-medium", Number(extra.price) === 0 && "text-muted-foreground")}>
                          {Number(extra.price) === 0 ? t("ui.common.free") : `+${formatPrice(extra.price, item.currency)}`}
                        </span>
                      </li>))}
                  </ul>
                </div>))}
            </div>)}
        </div>

        <DrawerFooter className="pt-2">
          <Button disabled={isSoldOut} onClick={() => router.visit(`/orders/new?item_id=${item.id}`)}>
            {isSoldOut ? t("ui.menu.item_sold_out") : t("ui.menu.add_to_order")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </ResponsiveDrawer>)
}
